/**
 * NotificationBell — @mention notifications dropdown
 */
import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, AtSign, Check, CheckCheck, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';

const WE = {
  bgSurface: '#FFFFFF', bgPage: '#FAFAF8', bgWarm: '#FFF9F0', bgSubtle: '#F5F3EF',
  border: '#E8E5E0', borderLight: '#F0EDE8',
  textPrimary: '#1C1917', textSecondary: '#57534E', textMuted: '#A8A29E',
  gold: '#C17F0A',
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  const fetchNotifications = async () => {
    try {
      const { data } = await api.get('/notifications');
      setNotifications(data.notifications || []);
      setUnreadCount(data.unread_count || 0);
    } catch (e) { /* silent */ }
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (id) => {
    await api.put('/notifications/' + id + '/read');
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllRead = async () => {
    await api.put('/notifications/read-all');
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);
  };

  const handleClick = (notif) => {
    if (!notif.is_read) markRead(notif.id);
    if (notif.company_name) {
      navigate('/leads/' + encodeURIComponent(notif.company_name));
      setOpen(false);
    }
  };

  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'upravo';
    if (mins < 60) return mins + ' min';
    const hours = Math.floor(mins / 60);
    if (hours < 24) return hours + 'h';
    return Math.floor(hours / 24) + 'd';
  };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => { setOpen(v => !v); if (!open) fetchNotifications(); }}
        style={{
          position: 'relative', width: '36px', height: '36px', borderRadius: '8px',
          border: '1px solid ' + WE.border, background: open ? WE.bgWarm : WE.bgSurface,
          cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        <Bell size={16} style={{ color: unreadCount > 0 ? WE.gold : WE.textMuted }} />
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: '-4px', right: '-4px', minWidth: '16px', height: '16px',
            borderRadius: '8px', background: '#DC2626', color: '#FFF', fontSize: '9px', fontWeight: 700,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px',
            border: '2px solid ' + WE.bgSurface,
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'absolute', top: '100%', right: 0, marginTop: '8px',
              width: '340px', maxHeight: '420px', overflow: 'hidden',
              background: WE.bgSurface, borderRadius: '12px', border: '1px solid ' + WE.border,
              boxShadow: '0 8px 32px rgba(120,100,60,0.18)', zIndex: 100,
              display: 'flex', flexDirection: 'column',
            }}
          >
            {/* Header */}
            <div style={{ padding: '12px 16px', borderBottom: '1px solid ' + WE.borderLight, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: WE.textPrimary }}>Obavijesti</span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                {unreadCount > 0 && (
                  <button onClick={markAllRead} style={{
                    fontSize: '10px', fontWeight: 600, color: WE.gold, background: 'none', border: 'none',
                    cursor: 'pointer', fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    <CheckCheck size={12} /> Označi sve
                  </button>
                )}
                <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: WE.textMuted, padding: '2px' }}>
                  <X size={14} />
                </button>
              </div>
            </div>

            {/* List */}
            <div style={{ flex: 1, overflow: 'auto' }}>
              {notifications.length === 0 ? (
                <div style={{ padding: '32px 16px', textAlign: 'center' }}>
                  <Bell size={20} style={{ margin: '0 auto 8px', color: WE.border }} />
                  <p style={{ fontSize: '12px', color: WE.textMuted }}>Nema obavijesti</p>
                </div>
              ) : (
                notifications.map(notif => (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    style={{
                      display: 'flex', gap: '10px', padding: '10px 16px', width: '100%',
                      background: notif.is_read ? 'transparent' : WE.bgWarm,
                      border: 'none', borderBottom: '1px solid ' + WE.borderLight,
                      cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit',
                      transition: 'background 0.12s',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = WE.bgPage}
                    onMouseLeave={e => e.currentTarget.style.background = notif.is_read ? 'transparent' : WE.bgWarm}
                  >
                    <div style={{
                      width: '28px', height: '28px', borderRadius: '50%', flexShrink: 0,
                      background: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <AtSign size={13} style={{ color: WE.gold }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12px', fontWeight: notif.is_read ? 500 : 700, color: WE.textPrimary, marginBottom: '2px' }}>
                        {notif.title || 'Obavijest'}
                      </div>
                      <div style={{ fontSize: '11px', color: WE.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {notif.body || ''}
                      </div>
                      <div style={{ fontSize: '10px', color: WE.textMuted, marginTop: '2px' }}>
                        {notif.created_by && <span style={{ fontWeight: 600 }}>{notif.created_by.split('@')[0]}</span>}
                        {notif.created_by && ' \u00B7 '}
                        {timeAgo(notif.created_at)}
                      </div>
                    </div>
                    {!notif.is_read && (
                      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: WE.gold, flexShrink: 0, marginTop: '6px' }} />
                    )}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
